import { LoadingCard } from '@/components/Base/LoadingCard/LoadingCard'
import { NoDataToDisplayCard } from '@/components/Base/NoDataToDisplayCard/NoDataToDisplayCard'
import Breadcrumb from '@/components/CustomBreadCrumb/BreadCrumb'
import { GFPAFactorContributionChart } from '@/components/GFPA/FactorContribution/FactorContributionChart'
import { GFPAFactorContributionTable } from '@/components/GFPA/FactorContribution/FactorContributionTable'
import {
  GFPADefaultFactorContributionTableColumns,
  GFPAFactorContributionTableColumnsWithCurrency,
} from '@/components/GFPA/FactorContribution/FactorContributionTableColumns'
import {
  GFRDefaultFactorContributionTableColumns,
  GFRFactorContributionTableColumnsWithCurrency,
} from '@/components/GFPA/FactorContribution/GFRFactorContributionTableColumns'
import {
  FACTOR_CONTRIBUTION_DEFAULT_SORT_KEY,
  FACTOR_CONTRIBUTION_DRILLDOWN_KEY_L1,
  FACTOR_CONTRIBUTION_DRILLDOWN_KEY_L2,
} from '@/constants/common.constant'
import { AxiomReportType } from '@/enums/axiom-report.enum'
import { useAppSelector } from '@/hooks/use-app-selector'
import {
  IBreadcrumbItemCategorised,
  IGfpaFactorContribution,
  IGfpaFactorContributionChartData,
} from '@/interfaces/gfpaReport.interface'
import { getGFPAFactorContributionReport } from '@/services/axiomReport.service'
import { getTransformedFactorContributionChartData } from '@/utils/grid-utils/gfpaGridUtils'
import { checkVariantsInKey, sortArray } from '@/utils/utils'
import { ColDef } from '@ag-grid-community/core'
import Box from '@mui/material/Box'
import { cloneDeep } from 'lodash'
import React, { useEffect, useState } from 'react'

const chartHandler: any = {
  currencyCode: null,
  factorSubCategoryCode: null,
  breadcrumbs: [],
  filteredCurrencyData: [],
  filteredFactorSubCategoryData: [],
}

export const GFPAFactorContributionPage: React.FC<{
  setGridReference: Function
}> = ({ setGridReference }) => {
  const selectedReport = useAppSelector((s) => s.axiomReport.selectedReport)
  const [rowData, setRowData] = useState<IGfpaFactorContribution[]>([])
  const [chartData, setChartData] = useState<
    IGfpaFactorContributionChartData[]
  >([])
  const [filteredDataForDrillDown, setFilteredDataForDrillDown] = useState<
    IGfpaFactorContribution[]
  >([])
  const [tableColumns, setTableColumns] = useState<ColDef[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [chartBreadcrumbs, setChartBreadcrumbs] = useState<
    IBreadcrumbItemCategorised[]
  >([])
  const [multipleCurrencyExists, setMultipleCurrencyExistance] = useState(false)

  useEffect(() => {
    const fetchFactorContribution = async () => {
      try {
        setLoading(true)
        const response = await getGFPAFactorContributionReport(
          selectedReport.reportId,
          selectedReport.reportType.toLowerCase(),
        )
        if (response?.length) {
          // Sort Response Data based upon Default Sorting Index
          const sortedResponse = sortArray(
            response,
            FACTOR_CONTRIBUTION_DEFAULT_SORT_KEY,
            'asc',
          )

          // Check if Multiple variations of Level 1 Key exists
          const shouldCurrencyCodeColumnExist = checkVariantsInKey(
            sortedResponse,
            FACTOR_CONTRIBUTION_DRILLDOWN_KEY_L1,
          )

          setMultipleCurrencyExistance(shouldCurrencyCodeColumnExist)

          // Get GFPA ColumnDefs based on *shouldCurrencyCodeColumnExist*
          const GFPAFactorContributionTableColumns =
            shouldCurrencyCodeColumnExist
              ? GFPAFactorContributionTableColumnsWithCurrency
              : GFPADefaultFactorContributionTableColumns

          // Get GFR ColumnDefs based on *shouldCurrencyCodeColumnExist*
          const GFRFactorContributionTableColumns =
            shouldCurrencyCodeColumnExist
              ? GFRFactorContributionTableColumnsWithCurrency
              : GFRDefaultFactorContributionTableColumns

          // Define Column Definations for AG Grid based upon Report Type
          const columDef =
            selectedReport.reportType === AxiomReportType.GFR
              ? GFRFactorContributionTableColumns
              : GFPAFactorContributionTableColumns

          const finalColDefs = [...columDef]

          setTableColumns(finalColDefs)
          const chartTransformData = getTransformedFactorContributionChartData(
            sortedResponse,
            shouldCurrencyCodeColumnExist
              ? FACTOR_CONTRIBUTION_DRILLDOWN_KEY_L1
              : FACTOR_CONTRIBUTION_DRILLDOWN_KEY_L2,
          )
          setRowData(sortedResponse)
          setFilteredDataForDrillDown(sortedResponse)
          setChartData(chartTransformData)
        }
      } catch (err) {
        console.warn(err)
      } finally {
        setLoading(false)
      }
    }
    if (Object.keys(selectedReport).length) {
      fetchFactorContribution()
    }
  }, [selectedReport])

  const handleBreadcrumbClick = (item: IBreadcrumbItemCategorised) => {
    let chartTData: any = []

    if (item.category === 'all') {
      chartTData = getTransformedFactorContributionChartData(
        rowData,
        multipleCurrencyExists ? 'currency_code' : 'factor_sub_category',
      )
    } else if (item.category === 'currency_code') {
      chartTData = getTransformedFactorContributionChartData(
        chartHandler?.filteredCurrencyData,
        'factor_sub_category',
      )
    } else if (item.category === FACTOR_CONTRIBUTION_DRILLDOWN_KEY_L2) {
      chartTData = getTransformedFactorContributionChartData(
        chartHandler?.filteredFactorSubCategoryData,
        'factor_name',
      )
    }

    setChartBreadcrumbs((bc) => bc?.splice(0, item.currentLevel + 1))
    setChartData(chartTData)
  }

  const handleCurrencyClick = (currencyCode: string) => {
    const selectedCurrencyCategoryList = filteredDataForDrillDown.filter(
      (item: IGfpaFactorContribution) => item.currency_code === currencyCode,
    )
    const chartTransformData = getTransformedFactorContributionChartData(
      selectedCurrencyCategoryList,
      'factor_sub_category',
    )
    const bcList = [
      { label: 'Currency', category: 'all', currentLevel: 0 },
      { label: currencyCode, category: 'currency_code', currentLevel: 1 },
    ]

    chartHandler.breadcrumbs = [...bcList]
    chartHandler.currencyCode = currencyCode
    chartHandler.filteredCurrencyData = cloneDeep(selectedCurrencyCategoryList)

    setChartData(cloneDeep(chartTransformData))
    setFilteredDataForDrillDown(selectedCurrencyCategoryList)
    setChartBreadcrumbs(bcList)
  }

  const handlefactorCategoryClick = (factorSubCategory: string) => {
    if (multipleCurrencyExists) {
      const selectedFactorSubCategoryList =
        chartHandler?.filteredCurrencyData?.filter(
          (item: IGfpaFactorContribution) =>
            item.factor_sub_category === factorSubCategory,
        )

      const chartTransformData = getTransformedFactorContributionChartData(
        selectedFactorSubCategoryList,
        'factor_name',
      )
      const bcList = [
        ...chartHandler?.breadcrumbs,
        {
          label: factorSubCategory,
          category: 'factor_category',
          currentLevel: chartBreadcrumbs.length,
        },
      ]
      chartHandler.breadcrumbs = [...bcList]
      chartHandler.filteredFactorSubCategoryData = cloneDeep(
        selectedFactorSubCategoryList,
      )

      setChartData(chartTransformData)
      setChartBreadcrumbs(bcList)
    } else {
      const selectedFactorSubCategoryList = rowData.filter(
        (item: IGfpaFactorContribution) =>
          item.factor_sub_category === factorSubCategory,
      )

      const chartTransformData = getTransformedFactorContributionChartData(
        selectedFactorSubCategoryList,
        'factor_name',
      )
      const bcList = [
        {
          label: 'Factor Sub Category',
          category: 'all',
          currentLevel: 0,
        },
        {
          label: factorSubCategory,
          category: 'factor_sub_category',
          currentLevel: 1,
        },
      ]
      chartHandler.breadcrumbs = [...bcList]
      chartHandler.filteredFactorSubCategoryData = cloneDeep(
        selectedFactorSubCategoryList,
      )

      setChartData(chartTransformData)
      setChartBreadcrumbs(bcList)
    }
  }

  if (loading) {
    return <LoadingCard />
  } else if (rowData.length) {
    return (
      <Box
        display='grid'
        height='100%'
        gridTemplateRows='repeat(2, auto)'
        gap={4}
        boxSizing='border-box'
        pt={0}
      >
        <Breadcrumb
          items={chartBreadcrumbs}
          handleBreadcrumbClick={handleBreadcrumbClick}
        />
        <GFPAFactorContributionChart
          reportType={selectedReport.reportType}
          chartData={chartData}
          handleCurrencySelection={handleCurrencyClick}
          handleFactorCategorySelection={handlefactorCategoryClick}
        />
        <GFPAFactorContributionTable
          rowData={rowData}
          columnsList={tableColumns}
          setGridReference={setGridReference}
        />
      </Box>
    )
  } else {
    return <NoDataToDisplayCard />
  }
}
